export const REGISTRATION_CUTOFF_HOURS = [1, 2, 12, 24, 48] as const;

export type RegistrationCutoffHours = (typeof REGISTRATION_CUTOFF_HOURS)[number];

export function isRegistrationCutoffHours(value: unknown): value is RegistrationCutoffHours {
  return (
    typeof value === 'number' &&
    REGISTRATION_CUTOFF_HOURS.includes(value as RegistrationCutoffHours)
  );
}

function formatHours(hours: number): string {
  return hours === 1 ? '1 hour' : `${hours} hours`;
}

// Select values are strings and Radix Select can't use '' as an item value, so
// "no cutoff" travels as 'none' in forms and null in the API.
export const NO_REGISTRATION_CUTOFF = 'none';

export const REGISTRATION_CUTOFF_OPTIONS: Array<{ value: string; label: string }> = [
  { value: NO_REGISTRATION_CUTOFF, label: 'No cutoff (open until the session starts)' },
  ...REGISTRATION_CUTOFF_HOURS.map(hours => ({
    value: String(hours),
    label: `${formatHours(hours)} before`,
  })),
];

export function registrationCutoffToFormValue(hours: number | null | undefined): string {
  return hours ? String(hours) : NO_REGISTRATION_CUTOFF;
}

export function registrationCutoffFromFormValue(value: string): number | null {
  const hours = Number(value);
  return isRegistrationCutoffHours(hours) ? hours : null;
}

export interface RegistrationCutoffSettings {
  registrationCutoffHours?: number | null;
  /** When true, the cutoff only applies to a session nobody has booked yet. */
  registrationCutoffOnlyIfEmpty?: boolean | null;
}

const HOUR_MS = 60 * 60 * 1000;

/** When the cutoff window opens. With no cutoff, that's the session start. */
export function getRegistrationClosesAt(
  startDateTime: Date | string,
  cutoffHours: number | null | undefined
): Date {
  const start = new Date(startDateTime).getTime();
  return new Date(start - (cutoffHours ?? 0) * HOUR_MS);
}

/**
 * Whether self-service booking is closed for a session. Always closed once the
 * session starts. Inside the cutoff window it's closed, unless the class only
 * cuts off empty sessions and someone has already booked this one.
 */
export function isRegistrationClosed(
  startDateTime: Date | string,
  settings: RegistrationCutoffSettings,
  enrolledCount: number,
  now: Date = new Date()
): boolean {
  const nowMs = now.getTime();
  if (nowMs >= new Date(startDateTime).getTime()) return true;
  if (!settings.registrationCutoffHours) return false;

  const closesAt = getRegistrationClosesAt(startDateTime, settings.registrationCutoffHours);
  if (nowMs < closesAt.getTime()) return false;

  return !(settings.registrationCutoffOnlyIfEmpty && enrolledCount > 0);
}

export function getRegistrationCutoffStatement(
  settings: RegistrationCutoffSettings
): string | null {
  const hours = settings.registrationCutoffHours;
  if (!hours) return null;
  return settings.registrationCutoffOnlyIfEmpty
    ? `Registration closes ${formatHours(hours)} before a session if no one has signed up yet.`
    : `Registration closes ${formatHours(hours)} before each session.`;
}
